import { create } from 'zustand';
import { AppState, AppAction, TierList, Tier, Item } from '../types';
import { nanoid } from './utils';

const getTierColors = (isDark: boolean) => ({
  S: isDark ? '#22c55e' : '#16a34a', // Green
  A: isDark ? '#84cc16' : '#65a30d', // Lime
  B: isDark ? '#eab308' : '#ca8a04', // Yellow
  C: isDark ? '#f97316' : '#ea580c', // Orange
  D: isDark ? '#ef4444' : '#dc2626', // Red
  F: isDark ? '#dc2626' : '#b91c1c', // Darker Red
});

const getDefaultTiers = (isDark: boolean): Omit<Tier, 'id' | 'items'>[] => {
  const colors = getTierColors(isDark);
  return [
    { name: 'S', color: colors.S },
    { name: 'A', color: colors.A },
    { name: 'B', color: colors.B },
    { name: 'C', color: colors.C },
    { name: 'D', color: colors.D },
    { name: 'F', color: colors.F },
  ];
};

const STORAGE_KEY = 'tier-list-app-state';

const loadState = (): AppState => {
  try {
    const savedState = localStorage.getItem(STORAGE_KEY);
    if (savedState) {
      return JSON.parse(savedState);
    }
  } catch (error) {
    console.error('Failed to load state from localStorage:', error);
  }
  return createInitialState();
};

const saveState = (state: AppState) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch (error) {
    console.error('Failed to save state to localStorage:', error);
  }
};

const createInitialState = (): AppState => ({
  tierLists: [],
  currentTierListId: null,
  history: [],
  future: [],
});

export const useTierListStore = create<{
  state: AppState;
  dispatch: (action: AppAction) => any;
}>((set) => ({
  state: loadState(),
  dispatch: (action) => {
    let result;
    set((store) => {
      const newState = reducer(store.state, action);
      saveState(newState);
      
      if (action.type === 'CREATE_TIER_LIST') {
        const newList = newState.tierLists[newState.tierLists.length - 1];
        result = newList;
      }
      
      return { state: newState };
    });
    return result;
  },
}));

const reducer = (state: AppState, action: AppAction): AppState => {
  switch (action.type) {
    case 'CREATE_TIER_LIST': {
      const isDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
      
      // Create tiers based on whether they're provided or need defaults
      let tiers;
      if (action.payload.tiers && action.payload.tiers.length > 0) {
        // Use provided tiers for imports
        tiers = action.payload.tiers.map(tier => ({
          ...tier,
          id: tier.id || nanoid(),
          items: (tier.items || []).map(item => ({
            ...item,
            id: item.id || nanoid()
          }))
        }));
      } else {
        // Use default tiers for new tier lists
        tiers = getDefaultTiers(isDark).map(tier => ({
          ...tier,
          id: nanoid(),
          items: [],
        }));
      }

      const unassignedItems = (action.payload.unassignedItems || []).map(item => ({
        ...item,
        id: item.id || nanoid()
      }));

      const newTierList: TierList = {
        id: nanoid(),
        title: action.payload.title,
        tiers,
        unassignedItems,
        createdAt: Date.now(),
        updatedAt: Date.now(),
      };

      return {
        ...state,
        tierLists: [...state.tierLists, newTierList],
        currentTierListId: newTierList.id,
      };
    }

    case 'DELETE_TIER_LIST': {
      return {
        ...state,
        tierLists: state.tierLists.filter((list) => list.id !== action.payload.id),
        currentTierListId:
          state.currentTierListId === action.payload.id
            ? null
            : state.currentTierListId,
      };
    }

    case 'UPDATE_TIER_LIST': {
      return {
        ...state,
        tierLists: state.tierLists.map((list) =>
          list.id === action.payload.id
            ? {
                ...list,
                title: action.payload.title,
                updatedAt: Date.now(),
              }
            : list
        ),
      };
    }

    case 'SELECT_TIER_LIST': {
      return {
        ...state,
        currentTierListId: action.payload.id,
      };
    }

    case 'ADD_TIER': {
      return {
        ...state,
        tierLists: state.tierLists.map((list) =>
          list.id === action.payload.tierListId
            ? {
                ...list,
                tiers: [
                  ...list.tiers,
                  { ...action.payload.tier, id: nanoid(), items: [] },
                ],
                updatedAt: Date.now(),
              }
            : list
        ),
      };
    }

    case 'UPDATE_TIER': {
      return {
        ...state,
        tierLists: state.tierLists.map((list) =>
          list.id === action.payload.tierListId
            ? {
                ...list,
                tiers: list.tiers.map((tier) =>
                  tier.id === action.payload.tier.id
                    ? { ...tier, ...action.payload.tier }
                    : tier
                ),
                updatedAt: Date.now(),
              }
            : list
        ),
      };
    }

    case 'DELETE_TIER': {
      return {
        ...state,
        tierLists: state.tierLists.map((list) =>
          list.id === action.payload.tierListId
            ? {
                ...list,
                tiers: list.tiers.filter(
                  (tier) => tier.id !== action.payload.tierId
                ),
                updatedAt: Date.now(),
              }
            : list
        ),
      };
    }

    case 'ADD_ITEM': {
      return {
        ...state,
        tierLists: state.tierLists.map((list) =>
          list.id === action.payload.tierListId
            ? {
                ...list,
                unassignedItems: [
                  ...list.unassignedItems,
                  { ...action.payload.item, id: nanoid() },
                ],
                updatedAt: Date.now(),
              }
            : list
        ),
      };
    }

    case 'UPDATE_ITEM': {
      return {
        ...state,
        tierLists: state.tierLists.map((list) =>
          list.id === action.payload.tierListId
            ? {
                ...list,
                tiers: list.tiers.map((tier) => ({
                  ...tier,
                  items: tier.items.map((item) =>
                    item.id === action.payload.item.id
                      ? { ...item, ...action.payload.item }
                      : item
                  ),
                })),
                unassignedItems: list.unassignedItems.map((item) =>
                  item.id === action.payload.item.id
                    ? { ...item, ...action.payload.item }
                    : item
                ),
                updatedAt: Date.now(),
              }
            : list
        ),
      };
    }

    case 'DELETE_ITEM': {
      return {
        ...state,
        tierLists: state.tierLists.map((list) =>
          list.id === action.payload.tierListId
            ? {
                ...list,
                tiers: list.tiers.map((tier) => ({
                  ...tier,
                  items: tier.items.filter(
                    (item) => item.id !== action.payload.itemId
                  ),
                })),
                unassignedItems: list.unassignedItems.filter(
                  (item) => item.id !== action.payload.itemId
                ),
                updatedAt: Date.now(),
              }
            : list
        ),
      };
    }

    case 'MOVE_ITEM': {
      const { tierListId, itemId, fromTierId, toTierId } = action.payload;
      return {
        ...state,
        tierLists: state.tierLists.map((list) => {
          if (list.id !== tierListId) return list;

          let item: Item | undefined;
          let newTiers = list.tiers;
          let newUnassignedItems = list.unassignedItems;

          // Remove item from source
          if (fromTierId === null) {
            item = list.unassignedItems.find((i) => i.id === itemId);
            newUnassignedItems = list.unassignedItems.filter(
              (i) => i.id !== itemId
            );
          } else {
            newTiers = list.tiers.map((tier) => {
              if (tier.id === fromTierId) {
                item = tier.items.find((i) => i.id === itemId);
                return {
                  ...tier,
                  items: tier.items.filter((i) => i.id !== itemId),
                };
              }
              return tier;
            });
          }

          if (!item) return list;

          // Add item to destination
          if (toTierId === null) {
            newUnassignedItems = [...newUnassignedItems, item];
          } else {
            newTiers = newTiers.map((tier) => {
              if (tier.id === toTierId) {
                return {
                  ...tier,
                  items: [...tier.items, item!],
                };
              }
              return tier;
            });
          }

          return {
            ...list,
            tiers: newTiers,
            unassignedItems: newUnassignedItems,
            updatedAt: Date.now(),
          };
        }),
      };
    }

    case 'REORDER_TIERS': {
      return {
        ...state,
        tierLists: state.tierLists.map((list) =>
          list.id === action.payload.tierListId
            ? {
                ...list,
                tiers: action.payload.tierIds.map((tierId) =>
                  list.tiers.find((t) => t.id === tierId)!
                ),
                updatedAt: Date.now(),
              }
            : list
        ),
      };
    }

    case 'REORDER_ITEMS': {
      return {
        ...state,
        tierLists: state.tierLists.map((list) =>
          list.id === action.payload.tierListId
            ? {
                ...list,
                tiers: list.tiers.map((tier) =>
                  tier.id === action.payload.tierId
                    ? {
                        ...tier,
                        items: action.payload.itemIds.map((itemId) =>
                          tier.items.find((item) => item.id === itemId)!
                        ),
                      }
                    : tier
                ),
                updatedAt: Date.now(),
              }
            : list
        ),
      };
    }

    default:
      return state;
  }
};